<script lang="ts">
	import { onMount } from 'svelte'

	interface Props {
		currentWidth: number
		minWidth?: number
		maxWidth?: number
		onResize: (newWidth: number) => void
		side?: 'left' | 'right'
	}

	let { currentWidth, minWidth = 150, maxWidth = 600, onResize, side = 'right' }: Props = $props()

	let isResizing = $state(false)
	let startX = $state(0)
	let startWidth = $state(0)

	const handleMouseDown = (e: MouseEvent) => {
		isResizing = true
		startX = e.clientX
		startWidth = currentWidth

		// Add body class for styling during resize
		document.body.classList.add('resizing')

		// Prevent text selection during resize
		e.preventDefault()
	}

	const handleMouseMove = (e: MouseEvent) => {
		if (!isResizing) return

		const deltaX = e.clientX - startX
		let newWidth: number

		if (side === 'left') {
			// For left side, moving left increases width, moving right decreases width
			newWidth = startWidth - deltaX
		} else {
			// For right side, moving right increases width, moving left decreases width
			newWidth = startWidth + deltaX
		}

		// Apply constraints
		const constrainedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth)

		onResize(constrainedWidth)
	}

	const handleMouseUp = () => {
		if (!isResizing) return

		isResizing = false
		document.body.classList.remove('resizing')
	}

	// Handle touch events for mobile support
	const handleTouchStart = (e: TouchEvent) => {
		if (e.touches.length !== 1) return

		isResizing = true
		startX = e.touches[0].clientX
		startWidth = currentWidth

		document.body.classList.add('resizing')
		e.preventDefault()
	}

	const handleTouchMove = (e: TouchEvent) => {
		if (!isResizing || e.touches.length !== 1) return

		const deltaX = e.touches[0].clientX - startX
		let newWidth: number

		if (side === 'left') {
			// For left side, moving left increases width, moving right decreases width
			newWidth = startWidth - deltaX
		} else {
			// For right side, moving right increases width, moving left decreases width
			newWidth = startWidth + deltaX
		}

		const constrainedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth)

		onResize(constrainedWidth)
	}

	const handleTouchEnd = () => {
		if (!isResizing) return

		isResizing = false
		document.body.classList.remove('resizing')
	}

	onMount(() => {
		// Add global mouse event listeners
		document.addEventListener('mousemove', handleMouseMove)
		document.addEventListener('mouseup', handleMouseUp)
		document.addEventListener('touchmove', handleTouchMove, { passive: false })
		document.addEventListener('touchend', handleTouchEnd)

		// Cleanup on unmount
		return () => {
			document.removeEventListener('mousemove', handleMouseMove)
			document.removeEventListener('mouseup', handleMouseUp)
			document.removeEventListener('touchmove', handleTouchMove)
			document.removeEventListener('touchend', handleTouchEnd)
			document.body.classList.remove('resizing')
		}
	})
</script>

<div
	class="resize-handle"
	class:left-side={side === 'left'}
	class:right-side={side === 'right'}
	class:active={isResizing}
	role="separator"
	aria-label="Resize sidebar"
	onmousedown={handleMouseDown}
	ontouchstart={handleTouchStart}
></div>

<style>
	.resize-handle {
		@apply absolute top-0 w-1 h-full cursor-col-resize bg-transparent transition-colors duration-200 z-10;
	}

	.resize-handle.right-side {
		@apply right-0 border-r-2 border-transparent;
	}

	.resize-handle.left-side {
		@apply left-0 border-l-2 border-transparent;
	}

	.resize-handle.right-side:hover {
		@apply border-blue-400;
	}

	.resize-handle.left-side:hover {
		@apply border-blue-400;
	}

	.resize-handle.right-side:active,
	.resize-handle.right-side.active {
		@apply border-blue-700;
	}

	.resize-handle.left-side:active,
	.resize-handle.left-side.active {
		@apply border-blue-700;
	}

	:global(body.resizing) {
		@apply cursor-col-resize select-none;
	}

	:global(body.resizing *) {
		pointer-events: none !important;
	}

	:global(body.resizing .resize-handle) {
		pointer-events: auto !important;
	}
</style>
